/**
 * Replace your own password: the current one, then the new one twice.
 *
 * Shared by the forced first-sign-in screen and My account. The server keeps
 * the session that sent it signed in and signs out every other one.
 */

import { useId, useState, type FormEvent } from 'react'
import { KeyRound } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import { Button, Field, Input } from '@/components/ui/primitives'

/** The server's limit, MIN_PASSWORD_LENGTH in core/security.py. */
export const MIN_PASSWORD_LENGTH = 6

export function ChangePasswordForm({
  onChanged,
  autoFocus = false,
  size = 'lg',
}: {
  onChanged: () => void | Promise<void>
  autoFocus?: boolean
  size?: 'md' | 'lg'
}) {
  const id = useId()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (next !== confirm) {
      setError('The new password and its confirmation do not match.')
      return
    }
    if (next.length < MIN_PASSWORD_LENGTH) {
      setError(`Your new password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }

    setBusy(true)
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next })
      setCurrent('')
      setNext('')
      setConfirm('')
      await onChanged()
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not change your password. Try again in a moment.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <Field label="Current password" htmlFor={`${id}-current`}>
        <Input
          id={`${id}-current`}
          type="password"
          autoComplete="current-password"
          autoFocus={autoFocus}
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          required
        />
      </Field>

      <Field label="New password" htmlFor={`${id}-next`}>
        <Input
          id={`${id}-next`}
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          invalid={Boolean(error)}
          required
        />
      </Field>

      <Field label="Confirm new password" htmlFor={`${id}-confirm`}>
        <Input
          id={`${id}-confirm`}
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          invalid={Boolean(error)}
          required
        />
      </Field>

      {error && (
        <p role="alert" className="text-[13px] text-[var(--danger)]">
          {error}
        </p>
      )}

      <Button
        type="submit"
        variant="primary"
        size={size}
        loading={busy}
        icon={<KeyRound className="h-4 w-4" />}
      >
        Change password
      </Button>
    </form>
  )
}
